import { format, getYear } from 'date-fns';
import { useState, useEffect, useContext } from 'react';
import { Box, Paper, Grid, Divider, Typography } from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttButtonCancel,
    SttButtonDelete,
    SttButtonSave,
    SttFacilityList,
    SttFormDate,
    SttFormSelect,
    SttFormText,
    SttLabelForm,
    SttBusySlotList,
    SttFullDialog,
    SttDialog,
} from '../../../sporttia/all';
import { BusySlotDialog } from './BusySlotDialog';
import ErrorsDialog, { BusyError } from './ErrorsDialog';
import DateBuilder from '../../../lib/DateBuilder';
import { getErrorMessage, safeFormatDate } from '../../../lib/utils';
import type { Error } from 'src/types/models/Error';
import { PERMISSIONS } from '../../../lib/permissions';

type Facility = {
    id: number;
    name: string;
};

type Slot = {
    id?: number;
    ini: string;
    end: string;
    weekdays: number[];
    numBusies?: number;
};

type SlotResponse = {
    busyScheduleSlot?: Slot;
    fails?: BusyError[];
};

type BusyScheduleType = {
    id?: number;
    name: string;
    status: string;
    ini: string;
    end: string;
    created?: string;
    facilities: Facility[] | null;
    slots: Slot[] | null;
    trash?: boolean;
};

type Props = {
    idBusySchedule?: string | number;
    trash?: boolean;
    onClose?: () => void;
};

export default function BusySchedule({
    idBusySchedule: propIdBusySchedule,
    trash: propTrash,
    onClose,
}: Props): React.ReactElement | null {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const { id } = useParams<{ id: string }>();
    const [busySchedule, setBusySchedule] = useState<BusyScheduleType | null>(
        null,
    );
    const [viewSlot, setViewSlot] = useState<Slot | null>(null);
    const [confirmationDialog, setConfirmationDialog] =
        useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [saveLoading, setSaveLoading] = useState<boolean>(false);
    const [title, setTitle] = useState<string | null>(null);
    const [slotErrors, setSlotErrors] = useState<BusyError[] | null>(null);
    const navigate = useNavigate();
    const { search } = useLocation();

    // Determine the actual ID and trash state
    const idBusySchedule = propIdBusySchedule || id;
    const trash =
        propTrash ?? (new URLSearchParams(search).get('trash') || false);

    useEffect(() => {
        // Skip if id contains ':' (unresolved URL parameter)
        if (!idBusySchedule || idBusySchedule.toString().includes(':')) {
            return;
        }

        if (idBusySchedule === 'create') {
            setBusySchedule({
                name: '',
                status: 'ACTIVE',
                ini: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
                end: `${getYear(new Date())}-12-31 23:59:59`,
                facilities: null,
                slots: null,
            });
            setTitle(t('New'));
        } else {
            loadBusySchedule();
        }
    }, [idBusySchedule]);

    function loadBusySchedule(): void {
        cxt?.api('GET', `/busies/schedules/${idBusySchedule}`, {
            params: {
                trash,
            },
            success: (response: { busySchedule: BusyScheduleType }) => {
                if (response.busySchedule) {
                    setBusySchedule(response.busySchedule);
                    setTitle(response.busySchedule.name);
                }
            },
        });
    }

    if (!busySchedule) {
        return null;
    }

    function handleChange({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void {
        if (!name) return;
        if (!busySchedule) return;
        setBusySchedule({ ...busySchedule, [name]: value });
    }

    function addFacility(facility: Facility): void {
        if (!busySchedule) return;
        const currentSchedule = busySchedule;
        cxt?.api(
            'POST',
            `/busies/schedules/${currentSchedule.id}/facilities/${facility.id}`,
            {
                success: () => {
                    setBusySchedule({
                        ...currentSchedule,
                        facilities: currentSchedule.facilities!.concat([
                            facility,
                        ]),
                    });
                },
            },
        );
    }

    function deleteFacility(facility: Facility): void {
        if (!busySchedule) return;
        const currentSchedule = busySchedule;
        cxt?.api(
            'DELETE',
            `/busies/schedules/${currentSchedule.id}/facilities/${facility.id}`,
            {
                confirmation: true,
                success: () => {
                    setBusySchedule({
                        ...currentSchedule,
                        facilities: currentSchedule.facilities!.reduce(
                            (result: Facility[], item) =>
                                item.id !== facility.id
                                    ? result.concat([item])
                                    : result,
                            [],
                        ),
                    });
                },
            },
        );
    }

    function saveSlot(slot: Slot): void {
        if (!busySchedule) return;
        const currentSchedule = busySchedule;

        if (slot.id) {
            setLoading(true);

            cxt?.api('PUT', `/busies/schedules/slots/${slot.id}`, {
                params: {
                    ini: slot.ini,
                    end: slot.end,
                    weekdays: slot.weekdays,
                },
                success: (response: { busyScheduleSlot: Slot }) => {
                    if (response.busyScheduleSlot) {
                        cxt?.showMessage('S', t('OperationSuccessful'));
                        setBusySchedule({
                            ...currentSchedule,
                            slots: currentSchedule.slots!.map((slt: any) =>
                                slt.id === response.busyScheduleSlot.id
                                    ? {
                                          ...response.busyScheduleSlot,
                                          ini: safeFormatDate(
                                              response.busyScheduleSlot.ini,
                                              'HH:mm',
                                          ),
                                          end: safeFormatDate(
                                              response.busyScheduleSlot.end,
                                              'HH:mm',
                                          ),
                                      }
                                    : slt,
                            ),
                        });
                        setViewSlot(null);
                    }
                    setLoading(false);
                },
                error: () => {
                    setLoading(false);
                },
            });
        } else {
            setLoading(true);

            cxt?.api('POST', `/busies/schedules/${currentSchedule.id}/slots`, {
                params: slot,
                success: (response: SlotResponse) => {
                    if (response.busyScheduleSlot) {
                        const addedSlot = {
                            ...response.busyScheduleSlot,
                            ini: safeFormatDate(
                                response.busyScheduleSlot.ini,
                                'HH:mm',
                            ),
                            end: safeFormatDate(
                                response.busyScheduleSlot.end,
                                'HH:mm',
                            ),
                        };
                        const slots = currentSchedule.slots
                            ? currentSchedule.slots.concat([addedSlot])
                            : [addedSlot];

                        setBusySchedule({ ...currentSchedule, slots });
                        setViewSlot(null);

                        if (response.fails && response.fails.length > 0) {
                            setSlotErrors(response.fails);
                        } else {
                            cxt?.showMessage('S', t('OperationSuccessful'));
                            setConfirmationDialog(true);
                        }
                    }

                    setLoading(false);
                },
                error: (response) => {
                    const responseError = response as Error;
                    cxt?.showMessage('E', responseError.error.msg);
                    setLoading(false);
                },
            });
        }
    }

    function deleteSlot(slot: Slot): void {
        if (!busySchedule) return;
        const currentSchedule = busySchedule;
        cxt?.api('DELETE', `/busies/schedules/slots/${slot.id}`, {
            confirmation: true,
            success: () => {
                cxt?.showMessage('S', t('Deleted'));
                setViewSlot(null);
                setBusySchedule({
                    ...currentSchedule,
                    slots: currentSchedule.slots!.filter(
                        (slt) => slt.id !== slot.id,
                    ),
                });
            },
        });
    }

    function saveBusySchedule(): void {
        if (!busySchedule) return;
        setSaveLoading(true);
        if (busySchedule.id) {
            const { name, ini, end, status } = busySchedule;
            cxt?.api('PUT', `/busies/schedules/${busySchedule.id}`, {
                params: { name, ini, end, status },
                success: () => {
                    cxt?.showMessage('S', t('OperationSuccessful'));
                    setTitle(name);
                    setSaveLoading(false);
                },
                error: (err: any) => {
                    const raw = getErrorMessage(err);
                    // El back envuelve las claves de traducción en [[...]]
                    const key = raw.replace(/^\[\[(.+)\]\]$/, '$1');
                    cxt.showMessage('E', t(key));
                    setSaveLoading(false);
                },
            });
        } else {
            const { name, ini, end, status } = busySchedule;
            cxt?.api('POST', `/scs/${cxt?.sc?.id}/busies/schedules`, {
                params: {
                    name,
                    ini: safeFormatDate(ini, 'yyyy-MM-dd'),
                    end: safeFormatDate(end, 'yyyy-MM-dd'),
                    status,
                },
                success: (response: { busySchedule: BusyScheduleType }) => {
                    if (response.busySchedule) {
                        setBusySchedule(response.busySchedule);
                        cxt?.showMessage('S', t('OperationSuccessful'));
                        setTitle(busySchedule.name);
                    }
                    setSaveLoading(false);
                },
                error: (err: any) => {
                    cxt.showMessage('E', getErrorMessage(err));
                    setSaveLoading(false);
                },
            });
        }
    }

    function deleteBusySchedule(): void {
        if (!busySchedule?.id) {
            return;
        }

        cxt?.api('DELETE', `/busies/schedules/${busySchedule.id}`, {
            confirmation: true,
            success: () => {
                cxt?.showMessage('S', t('Deleted'));
                if (onClose) {
                    onClose();
                } else {
                    navigate(-1);
                }
            },
        });
    }

    function openCreateSlot(): void {
        setViewSlot({
            ini: '09:00',
            end: '23:00',
            weekdays: [],
        });
    }

    return (
        <SttFullDialog
            open
            onClose={onClose || (() => navigate(-1))}
            title={title || undefined}
        >
            <Box p={3}>
                <Grid container spacing={3}>
                    {/* Left side - Detail controls */}
                    <Grid
                        size={{
                            xs: 12,
                            md: busySchedule.id && !busySchedule.trash ? 6 : 12,
                        }}
                    >
                        <Paper>
                            <Box p={3}>
                                <Grid container spacing={3}>
                                    {busySchedule.id && (
                                        <Grid size={12}>
                                            <SttLabelForm
                                                title={t('Created')}
                                                text={new DateBuilder(
                                                    busySchedule.created,
                                                    cxt?.sc?.timezone?.name,
                                                    cxt?.lang?.key,
                                                ).dmyhm()}
                                            />
                                        </Grid>
                                    )}

                                    <Grid size={12}>
                                        <SttFormText
                                            caption={t('Name')}
                                            name="name"
                                            defVal={busySchedule.name}
                                            onChange={handleChange}
                                        />
                                    </Grid>

                                    <Grid size={12}>
                                        <SttFormSelect
                                            caption={t('Status')}
                                            name="status"
                                            defVal={busySchedule.status}
                                            options={[
                                                {
                                                    caption: t('Active'),
                                                    value: 'ACTIVE',
                                                },
                                                {
                                                    caption: t('Inactive'),
                                                    value: 'INACTIVE',
                                                },
                                            ]}
                                            onChange={handleChange}
                                        />
                                    </Grid>

                                    <Grid size={12}>
                                        <Divider />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <SttFormDate
                                            caption={t('DateIni')}
                                            name="ini"
                                            defVal={busySchedule.ini}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <SttFormDate
                                            caption={t('DateEnd')}
                                            name="end"
                                            defVal={busySchedule.end}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Right side - Lists */}
                    {busySchedule.id && !busySchedule.trash && (
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Grid container spacing={3}>
                                <Grid size={12}>
                                    <Paper>
                                        <SttFacilityList
                                            permission={
                                                PERMISSIONS.CLOSURE.MODIFY
                                            }
                                            facilities={
                                                busySchedule.facilities || []
                                            }
                                            onAdd={(facility: Facility) =>
                                                addFacility(facility)
                                            }
                                            onDelete={(facility: Facility) =>
                                                deleteFacility(facility)
                                            }
                                            trash={
                                                typeof trash === 'boolean'
                                                    ? trash
                                                    : false
                                            }
                                            status="ACTIVE,ONLY_SC"
                                        />
                                    </Paper>
                                </Grid>

                                <Grid size={12}>
                                    <Paper>
                                        <SttBusySlotList
                                            slots={
                                                (busySchedule.slots ||
                                                    []) as any[]
                                            }
                                            onAdd={openCreateSlot}
                                            onSelect={(slot: any) =>
                                                setViewSlot(slot)
                                            }
                                        />
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>

                <Box className="mt-6 flex justify-start gap-4 flex-wrap">
                    {busySchedule.id && !busySchedule.trash && (
                        <SttButtonDelete
                            onClick={deleteBusySchedule}
                            permission={PERMISSIONS.CLOSURE.DELETE}
                        />
                    )}

                    {!busySchedule.trash && (
                        <SttButtonSave
                            loading={saveLoading}
                            onClick={saveBusySchedule}
                            permission={
                                busySchedule.id
                                    ? PERMISSIONS.CLOSURE.MODIFY
                                    : PERMISSIONS.CLOSURE.CREATE
                            }
                        />
                    )}

                    {!busySchedule.id && (
                        <SttButtonCancel onClick={() => onClose?.()} />
                    )}
                </Box>

                <BusySlotDialog
                    item={viewSlot}
                    onSave={(slot: Slot) => saveSlot(slot)}
                    onDelete={(slot: Slot) => deleteSlot(slot)}
                    onClose={() => setViewSlot(null)}
                    loading={loading}
                    parentIni={busySchedule.ini}
                    parentEnd={busySchedule.end}
                />
                {confirmationDialog && (
                    <SttDialog
                        open
                        buttons={[
                            {
                                type: 'close',
                                onClick: () => setConfirmationDialog(false),
                            },
                        ]}
                        title={t('generic.saved')}
                        content={
                            <Box p={2}>
                                <Box mb={2}>
                                    <Typography variant="body1">
                                        {t('busies.busiesCreationProcess')}
                                    </Typography>
                                </Box>
                            </Box>
                        }
                    />
                )}
                {slotErrors && (
                    <ErrorsDialog
                        errors={slotErrors}
                        onClose={() => setSlotErrors(null)}
                    />
                )}
            </Box>
        </SttFullDialog>
    );
}
